import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { format } from "date-fns";
import {
  ProfileHeader,
  ProfileStatCards,
  GeneralDetailsTab,
  SecurityTab,
  NotificationsTab,
  BillingTab,
  ActivityLogTab,
} from "@/components/profile";
import { useAuth } from "@/hooks/useAuth";
import { cn } from "@/lib/utils";
import { useLingui } from "@lingui/react/macro";
import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";

export const Route = createFileRoute("/(protected)/_layout/profile")({
  component: ProfileComponent,
});

type Tab = "general" | "security" | "notifications" | "billing" | "activity";

const TABS: { id: Tab; label: MessageDescriptor }[] = [
  { id: "general", label: msg`General Details` },
  { id: "security", label: msg`Security` },
  { id: "notifications", label: msg`Notifications` },
  { id: "billing", label: msg`Billing & Subscription` },
  { id: "activity", label: msg`Activity Log` },
];

function formatLastLogin(value?: string): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return format(date, "dd MMM yyyy, HH:mm");
}

function ProfileComponent() {
  const { t, i18n } = useLingui();
  const { user, isAuthenticated, isLoading } = useAuth();
  const [activeTab, setActiveTab] = useState<Tab>("general");

  if (isLoading || !isAuthenticated) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-(--color-text-secondary)">{t`Loading…`}</div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  const name =
    [user.firstName, user.lastName].filter(Boolean).join(" ") || user.email;
  const initials =
    name
      .split(" ")
      .filter(Boolean)
      .map((part) => part[0])
      .join("")
      .slice(0, 2)
      .toUpperCase() || "U";
  const role = user.roles?.[0]?.name ?? t`Member`;

  return (
    <div className="flex flex-col gap-5 p-3">
      <ProfileHeader
        name={name}
        email={user.email}
        initials={initials}
        avatarUrl={user.avatarUrl}
        statusLabel={t`Active`}
      />

      <ProfileStatCards
        role={role}
        accessScope={t`All`}
        lastLogin={formatLastLogin(user.lastLoginAt)}
        status={t`Active`}
      />

      <div className="h-px w-full bg-(--color-border)" />

      <div className="flex w-full flex-wrap gap-1 rounded-md bg-(--color-surface-muted) p-1">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            className={cn(
              "rounded-md px-3 py-2 text-sm transition-colors",
              activeTab === tab.id
                ? "bg-(--color-surface-content) font-medium text-(--color-text-primary) shadow-xs"
                : "font-normal text-(--color-text-secondary) hover:text-(--color-text-primary)",
            )}
          >
            {i18n._(tab.label)}
          </button>
        ))}
      </div>

      <div className="pt-1">
        {activeTab === "general" && <GeneralDetailsTab user={user} />}
        {activeTab === "security" && <SecurityTab />}
        {activeTab === "notifications" && <NotificationsTab />}
        {activeTab === "billing" && <BillingTab />}
        {activeTab === "activity" && <ActivityLogTab />}
      </div>
    </div>
  );
}
